import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ContextTabs, StatusBadge, EmptyState, UnknownState, LoadingState, ErrorState } from './osPrimitives';
import { MapPin, User, Crown, Sparkles, GitBranch, Navigation } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const humanize = (s) => (s || '').toString().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const money = (n) => (n === undefined || n === null || n === '') ? '—' : `$${Number(n).toLocaleString()}`;
const authHeaders = () => { const t = localStorage.getItem('ace_admin_token'); return { ...(t ? { Authorization: `Bearer ${t}` } : {}) }; };
async function getJSON(path) { const r = await fetch(`${API}${path}`, { credentials: 'include', headers: authHeaders() }); if (!r.ok) throw new Error(`${path} ${r.status}`); return r.json(); }

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'gps', label: 'GPS' }, { id: 'profiles', label: 'Profiles' }, { id: 'garage', label: 'Garage' },
  { id: 'decisions', label: 'Decisions' }, { id: 'consulting', label: 'Consulting' },
  { id: 'membership', label: 'Membership' }, { id: 'routes', label: 'Routes' },
];

export default function AutoLifestyleLive() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  return (
    <div className="os-animate" data-testid="autolifestyle-live">
      <ContextTabs tabs={TABS} active={tab} onChange={setTab} testid="al-workspace-tabs" />
      <div className="mt-4">
        {tab === 'overview' && <Overview navigate={navigate} onTab={setTab} />}
        {tab === 'gps' && <RouteState navigate={navigate} kind="gps" />}
        {tab === 'profiles' && <Profiles navigate={navigate} />}
        {tab === 'garage' && <Garage navigate={navigate} />}
        {tab === 'decisions' && <Decisions navigate={navigate} />}
        {tab === 'consulting' && <Consulting navigate={navigate} />}
        {tab === 'membership' && <Membership navigate={navigate} />}
        {tab === 'routes' && <RouteState navigate={navigate} kind="routes" />}
      </div>
    </div>
  );
}

function useLoad(path) {
  const [s, setS] = useState({ loading: true });
  const load = useCallback(() => { setS({ loading: true }); getJSON(path).then((d) => setS({ loading: false, d })).catch(() => setS({ loading: false, error: true })); }, [path]);
  useEffect(() => { load(); }, [load]);
  return { ...s, reload: load };
}

/* OVERVIEW — high-level counts composed from existing canonical aggregates (no new math) */
function Overview({ navigate, onTab }) {
  const garage = useLoad('/api/auto-lifestyle/garage');
  const members = useLoad('/api/command-center/ops/members');
  const clients = useLoad('/api/crm/clients');
  if (garage.loading || members.loading || clients.loading) return <LoadingState rows={4} />;
  const g = garage.d || {};
  const dec = g.decisions || {};
  const pendingDecisions = (dec.EVALUATING || 0) + (dec.REPLACE || 0) + (dec.SELL || 0) + (dec.LEASE_END || 0) + (dec.ADD || 0);
  const activeMembers = (members.d?.members || []).filter((m) => m.active).length;
  const totalClients = clients.d?.count ?? (clients.d?.clients || []).length;
  const leaseEvents = (g.lease_events || []).length;
  const metrics = [
    { key: 'garage', label: 'Clients w/ garage', value: g.clients_count || 0, tab: 'garage' },
    { key: 'vehicles', label: 'Garage vehicles', value: g.vehicle_count || 0, tab: 'garage' },
    { key: 'decisions', label: 'Decisions pending', value: pendingDecisions, tab: 'decisions', tone: pendingDecisions > 0 ? 'warning' : null },
    { key: 'lease', label: 'Lease/finance events', value: leaseEvents, tab: 'garage', tone: leaseEvents > 0 ? 'warning' : null },
    { key: 'members', label: 'Active members', value: activeMembers, tab: 'membership' },
    { key: 'clients', label: 'Clients on record', value: totalClients, tab: 'profiles' },
  ];
  return (
    <div data-testid="al-overview">
      <p className="text-[11px] mb-3" style={{ color: 'var(--os-muted)' }}>A live snapshot composed from canonical aggregates — garage, membership and CRM. No cross-client scoring or invented metrics.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-2 mb-5">
        {metrics.map((m) => (
          <button key={m.key} onClick={() => onTab(m.tab)} className="os-card p-3 text-left" data-testid={`al-overview-${m.key}`}>
            <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: 'var(--os-muted)' }}>{m.label}</p>
            <p className="os-display text-2xl" style={{ color: m.tone === 'warning' && m.value > 0 ? 'var(--os-warning)' : 'var(--os-text)' }}>{m.value}</p>
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        <button className="os-btn-ghost justify-start" onClick={() => onTab('garage')} data-testid="al-overview-go-garage"><GitBranch className="h-4 w-4" /> Garages</button>
        <button className="os-btn-ghost justify-start" onClick={() => onTab('decisions')} data-testid="al-overview-go-decisions"><Sparkles className="h-4 w-4" /> Decisions</button>
        <button className="os-btn-ghost justify-start" onClick={() => onTab('membership')} data-testid="al-overview-go-membership"><Crown className="h-4 w-4" /> Membership</button>
        <button className="os-btn-primary justify-start" onClick={() => navigate('/os/clients')} data-testid="al-overview-find-client"><User className="h-4 w-4" /> Find a client</button>
      </div>
    </div>
  );
}

/* GPS + ROUTES — no canonical workspace-wide journey feed exists; truthful state + Client 360 entry */
function RouteState({ navigate, kind }) {
  return (
    <div data-testid={`al-${kind}`}>
      <div className="os-card p-5">
        <div className="flex items-center gap-2 mb-2"><Navigation className="h-4 w-4" style={{ color: 'var(--os-red)' }} /><h3 className="font-bold" style={{ color: 'var(--os-text)' }}>{kind === 'gps' ? 'Auto Lifestyle GPS' : 'Route History'}</h3></div>
        <p className="text-sm" style={{ color: 'var(--os-muted)' }}>
          Journeys and routes (ALJ-* / ALR-*) are served per-client by the canonical GPS backend — a workspace-wide feed is not exposed by the current API, and route mode / next stop are never recomputed here. Open a client to review or open their active route.
        </p>
        <button className="os-btn-primary mt-4" onClick={() => navigate('/os/clients')} data-testid={`al-${kind}-open-clients`}><User className="h-4 w-4" /> Find a client</button>
      </div>
    </div>
  );
}

/* PROFILES — no ALP list endpoint; use canonical client roster as entry into per-client profiles */
function Profiles({ navigate }) {
  const { loading, d, error, reload } = useLoad('/api/crm/clients?limit=60');
  if (loading) return <LoadingState rows={4} />;
  if (error) return <ErrorState body="Client roster unavailable." onRetry={reload} />;
  const clients = d.clients || [];
  return (
    <div data-testid="al-profiles">
      <p className="text-[11px] mb-3" style={{ color: 'var(--os-muted)' }}>Auto Lifestyle profiles (ALP-*) open per client in Client 360. No cross-client scoring is computed here.</p>
      {!clients.length ? <EmptyState title="No clients on record" /> :
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="al-profiles-list">
          {clients.map((c) => <div key={c.client_id} className="os-card p-4" data-testid={`al-profile-${c.client_id}`}>
            <p className="font-semibold text-sm truncate" style={{ color: 'var(--os-text)' }}>{c.name || 'Client'}</p>
            <p className="text-xs" style={{ color: 'var(--os-muted)' }}>{c.client_id} · {humanize(c.segment) || 'General'} · {c.total_bookings || 0} services</p>
            <div className="flex gap-2 mt-3">
              <button className="os-btn-primary" style={{ minHeight: 34, padding: '6px 12px' }} onClick={() => navigate(`/os/clients/${c.client_id}`)} data-testid={`al-profile-open-${c.client_id}`}>Open Client</button>
              <button className="os-btn-ghost" style={{ minHeight: 34, padding: '6px 12px' }} onClick={() => navigate(`/os/clients/${c.client_id}`)}>View Profile</button>
            </div>
          </div>)}
        </div>}
    </div>
  );
}

/* GARAGE — live canonical aggregate (auto_lifestyle_garage) */
function Garage({ navigate }) {
  const { loading, d, error, reload } = useLoad('/api/auto-lifestyle/garage');
  if (loading) return <LoadingState rows={3} />;
  if (error) return <ErrorState body="Garage workspace unavailable." onRetry={reload} />;
  const clients = d.clients_with_garage || [];
  const lease = d.lease_events || [];
  if (!clients.length) return (
    <div data-testid="al-garage">
      <div className="os-card p-5" data-testid="al-garage-notice">
        <div className="flex items-center gap-2 mb-2"><GitBranch className="h-4 w-4" style={{ color: 'var(--os-warning)' }} /><h3 className="font-bold" style={{ color: 'var(--os-text)' }}>No client garages yet</h3></div>
        <p className="text-sm" style={{ color: 'var(--os-muted)' }}>Client garages are built in Client 360 → Driving. Add a client's vehicles there and they appear here. Garage vehicles are the client's own — never ACE fleet vehicles.</p>
        <button className="os-btn-primary mt-4" onClick={() => navigate('/os/clients')} data-testid="al-garage-open-driving"><User className="h-4 w-4" /> Find a client</button>
      </div>
    </div>
  );
  return (
    <div data-testid="al-garage">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <div className="os-card p-3"><p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: 'var(--os-muted)' }}>Clients w/ garage</p><p className="os-display text-2xl" style={{ color: 'var(--os-text)' }}>{d.clients_count}</p></div>
        <div className="os-card p-3"><p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: 'var(--os-muted)' }}>Vehicles</p><p className="os-display text-2xl" style={{ color: 'var(--os-text)' }}>{d.vehicle_count}</p></div>
        <div className="os-card p-3"><p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: 'var(--os-muted)' }}>Decisions pending</p><p className="os-display text-2xl" style={{ color: 'var(--os-text)' }}>{(d.decisions?.EVALUATING || 0) + (d.decisions?.REPLACE || 0) + (d.decisions?.LEASE_END || 0)}</p></div>
        <div className="os-card p-3"><p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: 'var(--os-muted)' }}>Lease/finance events</p><p className="os-display text-2xl" style={{ color: 'var(--os-text)' }}>{lease.length}</p></div>
      </div>
      <p className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: 'var(--os-muted)' }}>Clients with a garage</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="al-garage-list">
        {clients.map((c) => <button key={c.client_id} className="os-card p-4 text-left" onClick={() => navigate(`/os/clients/${c.client_id}`)} data-testid={`al-garage-client-${c.client_id}`}>
          <p className="font-semibold text-sm" style={{ color: 'var(--os-text)' }}>{c.client_id}</p>
          <p className="text-xs" style={{ color: 'var(--os-muted)' }}>{c.vehicle_count} vehicle(s) · {c.garage_id}</p>
        </button>)}
      </div>
      {lease.length > 0 && <><p className="text-[10px] uppercase tracking-widest font-bold mt-5 mb-2" style={{ color: 'var(--os-muted)' }}>Lease / finance events</p>
        {lease.map((e, i) => <div key={i} className="os-card px-4 py-2.5 mb-2 flex items-center justify-between" data-testid="al-lease-event">
          <div><p className="text-sm" style={{ color: 'var(--os-text)' }}>{e.label} · {humanize(e.ownership_mode)}</p><p className="text-xs" style={{ color: 'var(--os-muted)' }}>{e.client_id}</p></div>
          <StatusBadge status={e.overdue ? 'risk' : 'attention'} label={e.date} />
        </div>)}</>}
    </div>
  );
}

/* DECISIONS — surface factual Garage decision states + consulting entry points */
function Decisions({ navigate }) {
  const pipe = useLoad('/api/consulting/pipeline');
  const garage = useLoad('/api/auto-lifestyle/garage');
  if (pipe.loading || garage.loading) return <LoadingState rows={2} />;
  const dec = garage.d?.decisions || {};
  const actionable = ['EVALUATING', 'REPLACE', 'SELL', 'LEASE_END', 'ADD'];
  const hasGarageDecisions = actionable.some((k) => dec[k]);
  return (
    <div data-testid="al-decisions">
      {hasGarageDecisions ? <>
        <p className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: 'var(--os-muted)' }}>Garage decision states</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid="al-decisions-garage">
          {actionable.filter((k) => dec[k]).map((k) => <div key={k} className="os-card p-3"><p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: 'var(--os-muted)' }}>{humanize(k)}</p><p className="os-display text-2xl" style={{ color: 'var(--os-text)' }}>{dec[k]}</p></div>)}
        </div>
      </> : <EmptyState title="No pending garage decisions" body="Own-vs-Access, Keep-vs-Replace and Driven-vs-Driving decisions come from client garages and consulting. Nothing is invented here." />}
      <div className="flex flex-wrap gap-2 mt-4">
        <button className="os-btn-primary" onClick={() => navigate('/os/clients')} data-testid="al-decisions-open-client"><User className="h-4 w-4" /> Open client garage</button>
        <button className="os-btn-ghost" onClick={() => navigate('/command-center')} data-testid="al-decisions-consult"><Sparkles className="h-4 w-4" /> Start Consultation</button>
      </div>
    </div>
  );
}

/* CONSULTING — reuse consulting_intakes */
function Consulting({ navigate }) {
  const { loading, d, error, reload } = useLoad('/api/consulting/intakes');
  if (loading) return <LoadingState rows={3} />;
  if (error) return <ErrorState body="Consulting intakes unavailable." onRetry={reload} />;
  const intakes = d.intakes || [];
  const tone = (s) => ({ open: 'waiting', in_progress: 'attention', completed: 'ready' }[(s || '').toLowerCase()] || 'unknown');
  return (
    <div data-testid="al-consulting">
      {!intakes.length ? <EmptyState title="No consulting intakes on record" body="Open, in-progress and completed engagements will appear here. Start one in the existing consultation tool." />
        : <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="al-consulting-list">
          {intakes.map((it, i) => <div key={it.intake_id || i} className="os-card p-4" data-testid={`al-intake-${it.intake_id || i}`}>
            <div className="flex items-center justify-between"><StatusBadge status={tone(it.status)} label={humanize(it.status)} /><span className="text-[10px] font-mono" style={{ color: 'var(--os-muted)' }}>{it.intake_id}</span></div>
            <p className="font-semibold text-sm mt-2" style={{ color: 'var(--os-text)' }}>{it.client_name || it.name || 'Client'}</p>
            <p className="text-xs" style={{ color: 'var(--os-muted)' }}>{humanize(it.engagement_type) || 'Consulting'}</p>
          </div>)}
        </div>}
      <button className="os-btn-primary mt-4" onClick={() => navigate('/command-center')} data-testid="al-consulting-open"><Sparkles className="h-4 w-4" /> Open Consultation Tool</button>
    </div>
  );
}

/* MEMBERSHIP — reuse ace_members */
function Membership({ navigate }) {
  const { loading, d, error, reload } = useLoad('/api/command-center/ops/members');
  if (loading) return <LoadingState rows={4} />;
  if (error) return <ErrorState body="Members unavailable." onRetry={reload} />;
  const members = d.members || [];
  return (
    <div data-testid="al-membership">
      {!members.length ? <EmptyState title="No members on record" /> :
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="al-membership-list">
          {members.map((m) => <div key={m.member_id} className="os-card p-4" data-testid={`al-member-${m.member_id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0"><p className="font-semibold text-sm truncate" style={{ color: 'var(--os-text)' }}>{m.name || 'Member'}</p>
                <p className="text-xs" style={{ color: 'var(--os-muted)' }}>{m.member_id}</p></div>
              <StatusBadge status={m.active ? 'ready' : 'unknown'} label={m.active ? 'Active' : 'Inactive'} />
            </div>
            <div className="flex items-center gap-2 mt-2">
              <span className="os-chip inline-flex items-center gap-1" style={{ color: 'var(--os-warning)', border: '1px solid var(--os-border)' }}><Crown className="h-3 w-3" />{humanize(m.tier_meta?.name || m.tier)}</span>
              {m.tier_meta?.monthly_fee != null && <span className="text-[11px]" style={{ color: 'var(--os-muted)' }}>{money(m.tier_meta.monthly_fee)}/mo</span>}
            </div>
            <div className="flex gap-2 mt-3">
              <button className="os-btn-primary" style={{ minHeight: 34, padding: '6px 12px' }} onClick={() => navigate(`/os/clients/${encodeURIComponent(m.email || m.name || '')}`)} data-testid={`al-member-open-${m.member_id}`}>Open Client 360</button>
              <button className="os-btn-ghost" style={{ minHeight: 34, padding: '6px 12px' }} onClick={() => navigate('/membership')}>Review</button>
            </div>
          </div>)}
        </div>}
    </div>
  );
}
